<template>
    <div class="send-http-post-process-modal">
        <ProcessModal
            data-qa="send-http-post-process-modal-process"
            :is-open="isOpen"
            :title="$t('funnel.edit.send.http.post.title')"
            :summary-text="$t('funnel.edit.send.http.post.summary')"
            show-save
            :process-tip-text="$t('funnel.edit.send.http.post.tip.overview')"
            :process-tip-link="toolTipLink"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #additionalHeaderContent>
                <DsDropdown
                    ref="dropdown"
                >
                    <template #default>
                        <DsIconButton name="more-vertical" class="more-button" />
                    </template>

                    <template #menu>
                        <DsMultiselectMenu
                            v-model="menuCategory"
                            categorized
                            bind-value-only
                            :options="options"
                            class="custom-multiselect"
                            @selectCategory="setSelectCategory"
                            @input="handleInput"
                        />
                    </template>
                </DsDropdown>
            </template>
            <template #processParameters>
                <div class="vertical-container">
                    <div class="wrap-post-url-input">
                        <div class="send-http-url-input">
                            <MergeableInput
                                v-model="httpUrl"
                                data-qa="post-url-input-field"
                                :invalid="!isValidPostUrl"
                                required
                                :submitted="submitted"
                                :error-text="$t('funnel.edit.http.validation.url')"
                                :label="$t('funnel.edit.send.http.post.url.placeholder')"
                                :merge-field-categories="mergeFields"
                                class="post-url-input-field"
                            />
                        </div>
                    </div>

                    <span class="title-name-value-pair">
                        {{ $t('funnel.edit.send.http.post.name.value.title') }}
                    </span>
                    <div
                        v-for="(param, index) in params"
                        :key="index"
                        class="new-name-value-pair-param"
                    >
                        <div class="wrap-inputs">
                            <DsInputField
                                v-model="param.name"
                                :data-qa="param.name"
                                :label="$t('funnel.edit.send.http.post.name.placeholder')"
                                class="new-name-input-field"
                            />
                            <MergeableInput
                                v-model="param.value"
                                :data-qa="param.value"
                                :label="$t('funnel.edit.send.http.post.value.placeholder')"
                                class="new-value-input-field"
                                :merge-field-categories="mergeFields"
                            />
                        </div>
                        <span @click="removeBox(param.id)">
                            <DsIcon name="minus-circle" class="circle-button-remove" />
                        </span>
                    </div>

                    <div v-if="areParamsSet" class="new-name-value-pair-param">
                        <div class="wrap-inputs">
                            <DsInputField
                                v-model="nameParam"
                                :label="$t('funnel.edit.send.http.post.name.placeholder')"
                                class="new-name-input-field"
                                required
                                @blur="onBlurSaveParam"
                            />
                            <MergeableInput
                                v-model="valueParam"
                                :label="$t('funnel.edit.send.http.post.value.placeholder')"
                                class="new-value-input-field"
                                required
                                :merge-field-categories="mergeFields"
                                @blur-input="onBlurSaveParam"
                            />
                        </div>
                        <span @click="removeEmpty">
                            <DsIcon name="minus-circle" class="circle-button-remove" />
                        </span>
                    </div>

                    <span
                        class="add-field-span"
                        @click="showAddBox"
                    >
                        <DsIcon name="add" class="plus-button-add" />
                        <span class="add-name-value-pair">
                            {{ $t('funnel.edit.send.http.post.name.value.title') }}
                        </span>
                    </span>
                </div>
            </template>
        </ProcessModal>
        <HttpCopyFromCampaign
            v-if="showCopyFromCampaignListModal"
            :is-open="showCopyFromCampaignListModal"
            @close-copy-from-campaign-list="closeCopyFromCampaignListModal"
            @http-post-copy-from-campaign="fetchHttpPostFromCampaign"
        />
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import MergeableInput from '@/components/shared/MergeableInput.vue';
import HttpCopyFromCampaign from '../httpCopyFromCampaign/HttpCopyFromCampaign.vue';
import {
    createHttpPost,
    getHttpPostConfig,
    saveHttpPostConfig,
    getAuthorsMenus,
    convertToRequest,
    saveHttpPostAuthorTest,
    saveCopyFromCampaign,
} from './send.http.post.api';
import { getMergeFieldsForCB } from '../../../../configuration/task.flow.api';

const URL_PATTERN = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,16}\.?)(\/(~?[\w.-]+~?\/?)*)*\/?$/i;

export const FUNNEL_EDITOR_PAGE = '/app/funnel/funnelEditor?funnelId=';

export default {
    URL_PATTERN,
    components: {
        ProcessModal,
        MergeableInput,
        HttpCopyFromCampaign,
    },
    props: {
        funnelId: {
            type: Number,
            default: 0,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    fileBoxId: null,
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            isValidPostUrl: true,
            mergeFields: [],
            httpUrl: null,
            submitted: false,
            params: [],
            nameParam: null,
            valueParam: null,
            dataFileBoxId: null,
            menuCategory: null,
            options: [],
            showCopyFromCampaignListModal: false,
        };
    },

    computed: {
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        toolTipLink() {
            return this.isSpaApp ? this.$t('funnel.edit.send.http.post.tip.url.keap') : this.$t('funnel.edit.send.http.post.tip.url.classic');
        },
        areParamsSet() {
            return this.nameParam !== null && this.valueParam !== null;
        },
    },

    async mounted() {
        await this.fetchHttpFunnel();
        await this.createCategories();
        this.mergeFields = await getMergeFieldsForCB(this.funnelId);
    },

    methods: {
        setValidPostUrl() {
            this.isValidPostUrl = this.httpUrl?.match(this.$options.URL_PATTERN) !== null;
        },
        closeCopyFromCampaignListModal() {
            this.showCopyFromCampaignListModal = false;
        },
        async sendTest(userId) {
            try {
                await saveHttpPostAuthorTest(this.dataFileBoxId, userId);

                const successMessage = this.$t('funnel.edit.send.http.post.test.success.message');

                this.$toast({
                    message: successMessage,
                });
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.post.test.error.message');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        async convertToHttpRequest() {
            try {
                await convertToRequest(this.funnelId, this.dataFileBoxId);

                window.location.href = `${FUNNEL_EDITOR_PAGE}${this.funnelId}`;
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.post.menu.item.convert.campaign.error');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        async handleInput(data) {
            switch (this.selectedCategory) {
            case 0:
                await this.sendTest(data.value);
                break;
            case 1:
                this.showCopyFromCampaignListModal = true;
                break;
            case 2:
                await this.convertToHttpRequest();
                break;
            default:
                break;
            }
        },
        setSelectCategory(data) {
            this.selectedCategory = data.index;
        },
        async createCategories() {
            const dataMenus = await getAuthorsMenus();

            const { userMap } = dataMenus;

            const users = Object.entries(userMap).map(([value, label]) => {
                return { value, label };
            });

            this.options = [
                {
                    category: { value: 'actions', label: this.$t('funnel.edit.send.http.post.menu.item.user.test'), icon: 'user' },
                    options: users,
                },
                {
                    category: { value: 'actions', label: this.$t('funnel.edit.send.http.post.menu.item.copy.campaign'), icon: 'copy' },
                },
                {
                    category: { value: 'actions', label: this.$t('funnel.edit.send.http.post.menu.item.convert.campaign'), icon: 'replace' },
                },
            ];
        },
        async fetchHttpPostFromCampaign(id) {
            const { baseUrl, params } = await getHttpPostConfig(id);

            this.httpUrl = baseUrl;
            this.params = params.map((obj, index) => ({
                id: index,
                name: obj.name,
                value: obj.value,
            }));

            try {
                await saveCopyFromCampaign(this.dataFileBoxId, id);
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.post.copy.campaign.failed.message');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        async fetchHttpFunnel() {
            let tempFileBoxId = null;

            if (this.cell?.value?.fileBoxId) {
                tempFileBoxId = this.cell.value.fileBoxId;
            } else {
                tempFileBoxId = await createHttpPost();
            }

            const cell = { ...this.cell };

            cell.value = cell.value || {};

            cell.value.fileBoxId = tempFileBoxId;

            const { baseUrl, params } = await getHttpPostConfig(tempFileBoxId);

            this.httpUrl = baseUrl;
            this.dataFileBoxId = tempFileBoxId;
            this.params = params.map((obj, index) => ({
                id: index,
                name: obj.name,
                value: obj.value,
            }));
        },
        showAddBox() {
            if (this.nameParam === null) {
                this.nameParam = '';
            }

            if (this.valueParam === null) {
                this.valueParam = '';
            }
        },
        async updateConfig() {
            this.submitted = true;
            this.setValidPostUrl();

            if (!this.isValidPostUrl) {
                return;
            }
            const cell = { ...this.cell };

            cell.value = cell.value || {};


            cell.value.fileBoxId = this.dataFileBoxId;

            const copyParams = [...this.params];
            const payload = {
                baseUrl: this.httpUrl,
                params: {},
            };

            copyParams.forEach((param, index) => {
                const paramNameKey = `paramName_${index + 10}`;
                const paramValueKey = `paramValue_${index + 10}`;

                payload.params[paramNameKey] = param.name;
                payload.params[paramValueKey] = param.value;
            });

            payload.baseUrl = this.httpUrl;

            try {
                await saveHttpPostConfig(this.dataFileBoxId, payload);

                this.$emit('update-cell-value', { cell });

                this.submitted = false;
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.send.http.post.save.configuration');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        closeModal() {
            this.$emit('close-modal');
        },
        removeEmpty() {
            this.nameParam = null;
            this.valueParam = null;
        },
        removeBox(id) {
            if (id !== null) {
                this.params = this.params.filter((param) => {
                    return param.id !== id;
                });
            } else {
                this.nameParam = null;
                this.valueParam = null;
            }
        },
        onBlurSaveParam () {
            if (this.nameParam !== '' && this.valueParam !== '') {
                const newParamObj = {
                    id: this.params.length + 1,
                    name: this.nameParam,
                    value: this.valueParam,
                };

                this.params.push(newParamObj);
                this.nameParam = null;
                this.valueParam = null;
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.post-url-input-field {
    width: -webkit-fill-available;
    height: px-to-rem(70);
    max-width: px-to-rem(590);
    padding-top: px-to-rem(5);
}
</style>
<style lang="scss">
.vertical-container {
    display: flex;
    flex-direction: column;
}

.title-name-value-pair {
    margin-bottom: $spacing-250;
    font-weight: bold;
    padding-top: px-to-rem(20);
}

.new-name-value-pair-param {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.new-name-input-field {
    margin-right: $spacing-125;
}

.new-value-input-field {
    margin-right: $spacing-125;
}

.circle-button-remove {
    transform: translateY(-50%);
}

.add-field-span {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
    height: $spacing-400;
    margin-bottom: $spacing-250;
}

.plus-button-add, .add-name-value-pair {
    color: $color-blue-700;
}

.wrap-post-url-input {
    display: flex;
    flex-direction: column;
    margin-bottom: $spacing-250;
}

.send-http-url-input {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: px-to-rem(30);
    padding-top: $spacing-250;
}

.indicator-pound-sign {
    color: $color-blue-700;
    text-align: center;
    font-size: px-to-rem(18);
    transform: translateY(-50%);
    margin-left: $spacing-125;
}

.wrap-inputs {
    display: flex;
    flex-direction: row;
    width: 96%;
}

.custom-multiselect {
    white-space: nowrap;
}

</style>
